/**
 * CallNotesPanel -- slide-out panel for real-time note-taking during VoIP calls.
 *
 * Slides in from the right edge of the screen. Sits below the ActiveCallBar
 * (z-90) at z-85. Provides debounced autosave (1.2s) and persists notes both
 * to the call record and (on close) as a lead_note entry.
 */

import { X, ChevronDown, ChevronUp } from 'lucide-react'
import { voipApi, leadsApi } from '../../api/endpoints'
import { useQuery, useMutation } from '@tanstack/react-query'
import { useState, useEffect, useRef, useCallback } from 'react'

interface CallNotesPanelProps {
  isOpen: boolean
  onClose: () => void
  callId: number | null
  leadId: number | null
  leadName: string | null
}

type SaveStatus = 'idle' | 'saving' | 'saved' | 'error'

export default function CallNotesPanel({
  isOpen,
  onClose,
  callId,
  leadId,
  leadName,
}: CallNotesPanelProps) {
  const [notes, setNotes] = useState('')
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle')
  const [previousNotesOpen, setPreviousNotesOpen] = useState(false)

  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const notesRef = useRef(notes)
  notesRef.current = notes

  // ---------------------------------------------------------------------------
  // Fetch existing call notes when the panel opens for a given call
  // ---------------------------------------------------------------------------
  const { data: callData } = useQuery({
    queryKey: ['voip-call-status', callId],
    queryFn: () => voipApi.status(callId!),
    enabled: isOpen && callId !== null,
    refetchOnWindowFocus: false,
  })

  // Seed textarea with existing notes when fetched
  useEffect(() => {
    const existingNotes = callData?.data?.notes
    if (existingNotes) {
      setNotes(existingNotes)
    }
  }, [callData])

  // Reset state when callId changes
  useEffect(() => {
    setNotes('')
    setSaveStatus('idle')
    setPreviousNotesOpen(false)
  }, [callId])

  // ---------------------------------------------------------------------------
  // Fetch previous lead notes (3 most recent)
  // ---------------------------------------------------------------------------
  const { data: previousNotesData, isLoading: previousNotesLoading } = useQuery({
    queryKey: ['lead-notes', leadId],
    queryFn: () => leadsApi.getNotes(leadId!),
    enabled: isOpen && leadId !== null && previousNotesOpen,
    refetchOnWindowFocus: false,
  })

  const previousNotes = (previousNotesData?.data ?? []).slice(0, 3)

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------
  const updateNotesMutation = useMutation({
    mutationFn: (text: string) => voipApi.updateNotes(callId!, text),
    onMutate: () => setSaveStatus('saving'),
    onSuccess: () => setSaveStatus('saved'),
    onError: () => setSaveStatus('error'),
  })

  const saveToLeadMutation = useMutation({
    mutationFn: () => voipApi.saveNotesToLead(callId!),
  })

  // ---------------------------------------------------------------------------
  // Debounced autosave (1.2s after last keystroke)
  // ---------------------------------------------------------------------------
  const scheduleAutosave = useCallback(
    (text: string) => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
      if (callId === null) return

      setSaveStatus('idle')
      debounceTimer.current = setTimeout(() => {
        updateNotesMutation.mutate(text)
      }, 1200)
    },
    [callId], // eslint-disable-line react-hooks/exhaustive-deps
  )

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current)
    }
  }, [])

  // ---------------------------------------------------------------------------
  // Auto-resize textarea
  // ---------------------------------------------------------------------------
  useEffect(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = `${el.scrollHeight}px`
  }, [notes])

  // ---------------------------------------------------------------------------
  // Handle close: flush final save + persist to lead
  // ---------------------------------------------------------------------------
  const handleClose = useCallback(() => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current)

    if (callId !== null && notesRef.current.trim()) {
      updateNotesMutation.mutate(notesRef.current)
      saveToLeadMutation.mutate()
    }

    onClose()
  }, [callId, onClose]) // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------------
  // Handle text change
  // ---------------------------------------------------------------------------
  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value
    setNotes(value)
    scheduleAutosave(value)
  }

  // ---------------------------------------------------------------------------
  // Save status label
  // ---------------------------------------------------------------------------
  const statusLabel = (() => {
    switch (saveStatus) {
      case 'saving':
        return 'Saving...'
      case 'saved':
        return 'Saved'
      case 'error':
        return 'Save failed'
      default:
        return null
    }
  })()

  const statusColor = (() => {
    switch (saveStatus) {
      case 'saving':
        return 'text-gray-400'
      case 'saved':
        return 'text-green-500'
      case 'error':
        return 'text-red-500'
      default:
        return ''
    }
  })()

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <div
      className={`fixed top-0 right-0 h-full w-96 z-[85] bg-white border-l border-gray-200 shadow-xl flex flex-col pb-14 transition-transform duration-300 ease-in-out ${
        isOpen ? 'translate-x-0' : 'translate-x-full'
      }`}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900 truncate">
                Call Notes
              </h2>
              {statusLabel && (
                <span className={`text-xs ${statusColor} whitespace-nowrap`}>
                  {statusLabel}
                </span>
              )}
            </div>
            {leadName && (
              <p className="text-xs text-gray-500 truncate">{leadName}</p>
            )}
          </div>
        </div>
        <button
          onClick={handleClose}
          className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          title="Close notes panel"
        >
          <X size={18} />
        </button>
      </div>

      {/* Main textarea area */}
      <div className="flex-1 overflow-y-auto px-4 py-3">
        <textarea
          ref={textareaRef}
          value={notes}
          onChange={handleChange}
          placeholder="Type your notes here..."
          className="w-full min-h-[120px] resize-none rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
          style={{ overflow: 'hidden' }}
        />
      </div>

      {/* Previous Notes (collapsible) */}
      {leadId !== null && (
        <div className="shrink-0 border-t border-gray-200">
          <button
            onClick={() => setPreviousNotesOpen(!previousNotesOpen)}
            className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-600 hover:bg-gray-50 transition-colors"
          >
            <span className="font-medium">Previous Notes</span>
            {previousNotesOpen ? (
              <ChevronDown size={16} />
            ) : (
              <ChevronUp size={16} />
            )}
          </button>

          {previousNotesOpen && (
            <div className="px-4 pb-3 max-h-48 overflow-y-auto">
              {previousNotesLoading ? (
                <p className="text-xs text-gray-400 py-2">Loading...</p>
              ) : previousNotes.length === 0 ? (
                <p className="text-xs text-gray-400 py-2">
                  No previous notes for this lead.
                </p>
              ) : (
                <div className="space-y-2">
                  {previousNotes.map((note: Record<string, unknown>, idx: number) => (
                    <div
                      key={(note.id as number) ?? idx}
                      className="rounded-md bg-gray-50 border border-gray-100 px-3 py-2"
                    >
                      <p className="text-xs text-gray-700 whitespace-pre-wrap">
                        {(note.content as string) ?? (note.note as string) ?? ''}
                      </p>
                      {typeof note.created_at === 'string' && (
                        <p className="text-[11px] text-gray-400 mt-1">
                          {new Date(note.created_at as string).toLocaleDateString(undefined, {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
